/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { WitboostTemplate } from '@agilelab/plugin-wb-builder-common';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { Progress, WarningPanel } from '@backstage/core-components';
import { TemplateEntityV1beta3 } from '@backstage/plugin-scaffolder-common';
import { Box, Typography, makeStyles } from '@material-ui/core';
import { ReactNode } from 'react';
import { useScaffolderPageContext } from '../ScaffolderPage/context/useScaffolderPageContext';
import { CustomTemplate } from '../ScaffolderPage/utils';
import { TemplateCard } from './TemplateCard';

/**
 * @internal
 */
export type TemplateListProps = {
  group?: {
    title?: ReactNode;
    filter: (entity: any) => boolean;
  };
  witboostCustomTemplates: { [p: string]: CustomTemplate[] };
};

const useStyles = makeStyles(
  theme => ({
    root: {
      '&:first-child': {
        marginTop: theme.spacing(1),
      },
      '&:last-child': {
        marginBottom: 0,
      },
      marginBottom: theme.spacing(6),
    },
    cards: {
      paddingTop: theme.spacing(1),
      gap: theme.spacing(2),
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    },
  }),
  { name: 'TemplatesGroup' },
);

/**
 * @internal
 */
export const TemplateList = ({
  group,
  witboostCustomTemplates,
}: TemplateListProps) => {
  const { loading, error, entities, errors, TemplateCardComponent } =
    useScaffolderPageContext();

  const classes = useStyles();

  const CustomTemplateCard = TemplateCardComponent || TemplateCard;

  const maybeFilteredEntities = group
    ? entities.filter(e => group.filter(e))
    : entities;

  const titleComponent: ReactNode = (() => {
    let title: ReactNode = 'Other templates';
    if (group && group.title) {
      title = group.title;
    }

    if (typeof title === 'string') {
      return (
        <Typography
          style={{ fontWeight: 'bold' }}
          variant="h5"
          color="secondary"
        >
          {title}
        </Typography>
      );
    }

    return title;
  })();

  if (group && maybeFilteredEntities.length === 0) {
    return null;
  }
  return (
    <>
      {loading && <Progress />}

      {error && (
        <WarningPanel title="Oops! Something went wrong loading the templates">
          {error.message}
        </WarningPanel>
      )}

      <Box className={classes.root}>
        {titleComponent}
        <Box className={classes.cards}>
          {maybeFilteredEntities &&
            maybeFilteredEntities?.length > 0 &&
            maybeFilteredEntities.map((template: Entity) => (
              <CustomTemplateCard
                key={stringifyEntityRef(template)}
                error={errors?.find(e => e.entity_id === template.metadata.uid)}
                template={template as TemplateEntityV1beta3}
                customTemplates={
                  witboostCustomTemplates[
                    (
                      template as WitboostTemplate
                    ).spec.steps[0].input.values.useCaseTemplateId?.toLocaleLowerCase()
                  ]
                }
              />
            ))}
        </Box>
      </Box>
    </>
  );
};
